/**
 * TrailHistory — where each player has just been, as the *renderer* showed it.
 *
 * A movement trail is a directional cue: a short ribbon behind a player shows
 * which way they are moving and how fast, which is hard to read from a circle
 * on flat ground. The samples are taken from render positions (interpolated
 * for remote players, predicted for the local one), never from raw server
 * snapshots, so the trail always sits exactly behind the sprite the player sees.
 *
 * Pure data, no PIXI: the ring buffer here is what a trail *style* draws.
 * Sampling rule: record a point when the player has moved at least
 * `minDistance` since the last sample, or when `maxInterval` ms have passed
 * (so a standing player's trail fades out instead of freezing). Points older
 * than `lifeMs` are dropped; at most `maxSamples` are kept.
 */

export const TRAIL_DEFAULTS = Object.freeze({
  minDistance: 6,     // px between samples while moving
  maxInterval: 60,    // ms — force a sample so the tail keeps fading when stationary
  lifeMs: 500,        // how long a point stays visible
  maxSamples: 32
});

/**
 * The samples that lie behind a body rather than under it: a trail is drawn
 * below the sprite, so when the sprite is see-through (the local player's faded
 * invisibility view) the part of the trail beneath it would show through.
 * A sample within `radius` of the body centre counts as under it.
 * @param {Array<{x:number,y:number,t:number}>} samples
 * @returns {Array<{x:number,y:number,t:number}>} a new array; the input is untouched
 */
export function samplesOutsideBody(samples, x, y, radius) {
  return samples.filter((s) => Math.hypot(s.x - x, s.y - y) > radius);
}

export class TrailHistory {
  constructor(options = {}) {
    this.opts = { ...TRAIL_DEFAULTS, ...options };
    /** @type {Map<string, Array<{x:number,y:number,t:number}>>} */
    this.tracks = new Map();
  }

  /**
   * Record a rendered position for a player. Hidden players (invisibility) are
   * not sampled: a trail would give them away.
   * @returns {boolean} whether a sample was added
   */
  record(playerId, x, y, t, visible = true) {
    if (!visible) { this.tracks.delete(playerId); return false; }
    let track = this.tracks.get(playerId);
    if (!track) { track = []; this.tracks.set(playerId, track); }
    const last = track[track.length - 1];
    if (last) {
      const moved = Math.hypot(x - last.x, y - last.y) >= this.opts.minDistance;
      const stale = t - last.t >= this.opts.maxInterval;
      if (!moved && !stale) return false;
    }
    track.push({ x, y, t });
    if (track.length > this.opts.maxSamples) track.splice(0, track.length - this.opts.maxSamples);
    return true;
  }

  /** Drop expired points everywhere; players with no points left are forgotten. */
  prune(now) {
    const cutoff = now - this.opts.lifeMs;
    for (const [id, track] of this.tracks) {
      let i = 0;
      while (i < track.length && track[i].t < cutoff) i++;
      if (i > 0) track.splice(0, i);
      if (track.length === 0) this.tracks.delete(id);
    }
  }

  /** Points for a player, oldest first (empty array when none). */
  samplesFor(playerId) {
    return this.tracks.get(playerId) || [];
  }

  /** Forget a player (death, disconnect, teleport — the trail must not bridge the jump). */
  remove(playerId) {
    this.tracks.delete(playerId);
  }

  /** Forget everyone except the given ids (dead/disconnected players leave no ghost trail). */
  retainOnly(ids) {
    for (const id of this.tracks.keys()) if (!ids.has(id)) this.tracks.delete(id);
  }

  clear() {
    this.tracks.clear();
  }
}
